import { PriceZoneSystem } from '../enums/priceZoneSystem';

/**
 * The subset of a price zone this module needs. Kept structural so both the
 * website (whose zones come from the generated OpenAPI types) and the backend
 * (whose zones come out of the `priceZones` JSON column) can pass their own
 * concrete types without either side depending on the other's.
 */
export interface PriceZoneShape {
  system?: string;
  disabled?: boolean;
  products?: unknown[];
}

/**
 * The first non-disabled price zone matching the active system (`SH`/`ERNA`)
 * that also has at least one product, or `undefined` when there are none.
 * Shared by every price-placeholder consumer so zone selection stays
 * consistent between the Location Page and the City Page aggregation.
 *
 * The product check is deliberate: a zone can be enabled and match the system
 * while carrying no products at all, and selecting it would mask a later zone
 * that does have prices.
 */
export function findActivePriceZone<T extends PriceZoneShape>(
  zones: readonly T[] | null | undefined,
  system: PriceZoneSystem,
): T | undefined {
  return zones?.find(
    (zone) =>
      zone.system === system &&
      !zone.disabled &&
      (zone.products?.length ?? 0) > 0,
  );
}

interface PriceEntryShape {
  placeholder?: string | null;
  price: number;
}

interface PriceProductShape {
  prices?: PriceEntryShape[] | null;
}

/** The subset of a price zone `buildZonePriceMap` needs to flatten it. */
export interface FormattablePriceZoneShape {
  currency: string;
  products?: PriceProductShape[] | null;
}

/**
 * Formats a price with German locale (two decimals) and the zone's currency
 * symbol — e.g. `formatPrice(9.35, 'EUR')` → `"9,35 €"`. Non-EUR currencies
 * fall back to their raw code.
 */
/**
 * Reused: `toLocaleString(locales, options)` builds a fresh `Intl.NumberFormat`
 * on every call, ~50x the cost of formatting. A page render made ~1.7k of them.
 * Currency only picks the symbol below, so one instance covers every call.
 */
const PRICE_FORMAT = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatPrice(value: number, currency: string): string {
  const symbol = currency === 'EUR' ? '€' : currency;
  return `${PRICE_FORMAT.format(value)} ${symbol}`;
}

/**
 * Flat `placeholder → formatted price` map for a single zone, walking its
 * products and prices. First occurrence wins for duplicate placeholder names.
 */
export function buildZonePriceMap(
  zone: FormattablePriceZoneShape,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const product of zone.products ?? []) {
    for (const price of product.prices ?? []) {
      const placeholder = price.placeholder;
      if (placeholder && !map.has(placeholder)) {
        map.set(placeholder, formatPrice(price.price, zone.currency));
      }
    }
  }
  return map;
}
